import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer'
import { FC } from 'react'

import { AIPlan, AIPlanCustomSection } from 'api/interfaces/AIPlan'
import { AIPlanPersonasSection } from 'api/interfaces/AIPlanPersonasSection'
import { Persona } from 'api/interfaces/Persona'

Font.register({
  family: 'myriad-pro',
  fonts: [
    {
      src: require('../../../../../assets/fonts/myriad-pro-regular.woff'),
      fontWeight: 400
    },
    {
      src: require('../../../../../assets/fonts/myriad-pro-bold.woff'),
      fontWeight: 700
    }
  ]
})
Font.registerHyphenationCallback(word => [word])

interface AIPlanPersonasPdfProps {
  aiPlan: AIPlan
  personaSection?: AIPlanPersonasSection
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: '1.25in',
    paddingLeft: '1.75in',
    paddingBottom: '1.25in',
    paddingRight: '1.75in',
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  root: {
    fontFamily: 'myriad-pro',
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  header: {
    fontFamily: 'myriad-pro',
    fontSize: '26pt',
    lineHeight: 1.2,
    marginBottom: '10pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  section: {
    marginBottom: '4pt'
  },
  sectionTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '17pt',
    lineHeight: 1.2,
    marginTop: '30pt',
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  personaName: {
    fontFamily: 'myriad-pro',
    fontSize: '15pt',
    lineHeight: 1.2,
    marginTop: '30pt',
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  personaDescription: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  bulletContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  bulletColumn: {
    flex: 1,
    marginRight: 10
  },
  bulletItem: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  emptyState: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  sectionBox: {
    backgroundColor: '#E6EDF2',
    borderRadius: '8pt',
    padding: '12pt',
    marginTop: '20pt',
    marginBottom: '8pt'
  },
  boxTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  boxContent: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  }
})

const AIPlanPersonasPdf: FC<AIPlanPersonasPdfProps> = ({ aiPlan, personaSection }) => {
  const personas = aiPlan.personas || []
  const pageTitle = personaSection?.pageTitle || 'Persona Prioritization'
  const defaultIntroContent = 'The following personas are descriptive of the types of people we will be targeting. While fictional generalizations, they are based on research and market analysis. They will be used both in the targeting of your ad campaigns and in the crafting of your ads.'
  const introContent = personaSection?.introduction?.content || defaultIntroContent
  const customSections = personaSection?.abovePersonaList || []

  const renderBulletList = (items: string[]) => {
    const leftColumn = items.slice(0, 3)
    const rightColumn = items.slice(3, 6)

    return (
      <View style={styles.boxContent}>
        <View style={styles.bulletContainer}>
          <View style={styles.bulletColumn}>
            {leftColumn.map((item, index) => (
              <Text key={index} style={styles.bulletItem}>
                • {item}
              </Text>
            ))}
          </View>
          <View style={styles.bulletColumn}>
            {rightColumn.map((item, index) => (
              <Text key={index} style={styles.bulletItem}>
                • {item}
              </Text>
            ))}
          </View>
        </View>
      </View>
    )
  }

  const renderPersona = (persona: Persona, index: number) => (
    <View key={index}>
      <Text style={styles.personaName}>{persona.name}</Text>

      <Text style={styles.personaDescription}>{persona.description}</Text>

      {persona.demographics && (
        <View style={styles.sectionBox}>
          <Text style={styles.boxTitle}>Demographics</Text>
          {persona.demographics.ageRange && <Text style={styles.boxContent}>Age Range: {persona.demographics.ageRange}</Text>}
          {persona.demographics.gender && <Text style={styles.boxContent}>Gender: {persona.demographics.gender}</Text>}
          {persona.demographics.incomeRange && <Text style={styles.boxContent}>Income Range: {persona.demographics.incomeRange}</Text>}
          {persona.demographics.education && <Text style={styles.boxContent}>Education: {persona.demographics.education}</Text>}
          {persona.demographics.occupation && <Text style={styles.boxContent}>Occupation: {persona.demographics.occupation}</Text>}
        </View>
      )}

      {persona.triggers && persona.triggers.length > 0 && (
        <View style={styles.sectionBox}>
          <Text style={styles.boxTitle}>Buying Triggers</Text>
          {renderBulletList(persona.triggers)}
        </View>
      )}

      {persona.barriers && persona.barriers.length > 0 && (
        <View style={styles.sectionBox}>
          <Text style={styles.boxTitle}>Barriers</Text>
          {renderBulletList(persona.barriers)}
        </View>
      )}
    </View>
  )

  const renderCustomSection = (section: AIPlanCustomSection, index: number) => {
    if (!section || section.hideAll) return null

    return (
      <View key={`custom-${index}`} style={{ marginTop: '20pt', marginBottom: '8pt' }}>
        {section.showTitle && section.title && (
          <Text style={styles.sectionTitle}>{section.title}</Text>
        )}
        {section.content && (
          <Text style={styles.personaDescription}>{section.content}</Text>
        )}
      </View>
    )
  }

  const renderFirstPersonaWithTitle = (persona: Persona, index: number) => (
    <View key={`first-${index}`}>
      <View style={styles.section}>
        <Text style={styles.header}>{pageTitle}</Text>
      </View>
      {!personaSection?.introduction?.hideAll && (
        <Text style={styles.personaDescription}>{introContent}</Text>
      )}
      {customSections.map((section, idx) => renderCustomSection(section, idx))}
      {renderPersona(persona, index)}
    </View>
  )

  return (
    <Document>
      {personas.length > 0 ? (
        <>
          <Page size='LETTER' style={styles.page}>
            <View style={styles.root}>{renderFirstPersonaWithTitle(personas[0], 0)}</View>
          </Page>
          {personas.slice(1).map((persona, index) => {
            return (
              <Page key={index + 1} size='LETTER' style={styles.page}>
                <View style={styles.root}>{renderPersona(persona, index + 1)}</View>
              </Page>
            )
          })}
        </>
      ) : (
        <Page size='LETTER' style={styles.page}>
          <View style={styles.root}>
            <View style={styles.section}>
              <Text style={styles.header}>{pageTitle}</Text>
              {!personaSection?.introduction?.hideAll && (
                <Text style={styles.personaDescription}>{introContent}</Text>
              )}
              {customSections.map((section, idx) => renderCustomSection(section, idx))}
              <Text style={styles.emptyState}>No personas have been defined for this campaign yet.</Text>
            </View>
          </View>
        </Page>
      )}
    </Document>
  )
}

export default AIPlanPersonasPdf
